import { WinSlideProps, SLIDE_BACKGROUNDS } from '../onboarding.types';
import { PaginationDots } from '../PaginationDots';
import { LoadingSpinner } from '@/components/LoadingSpinner';

/**
 * Win Slide (Panel 3)
 *
 * Final onboarding screen with login CTAs:
 * "Consistency Pays. Literally."
 *
 * Design specs from panel3.html:
 * - Primary: uses app's primary color
 * - Background: #102210 (dark green)
 * - Icon container with glow effect
 * - Pagination dot glow effect
 */
export function WinSlide({ onLogin, onSkip, isLoading }: WinSlideProps) {
  return (
    <div
      className="relative flex h-dvh w-full overflow-hidden bg-[#102210] text-white"
      style={{ fontFamily: '"Lexend", sans-serif' }}
    >
      {/* Background Layer */}
      <div className="absolute inset-0 z-0 w-full h-full overflow-hidden">
        <div
          className="w-[110%] h-full bg-cover bg-center translate-x-[-5%]"
          style={{ backgroundImage: `url('${SLIDE_BACKGROUNDS.win}')` }}
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-[#102210]/60 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#102210]/30 via-transparent to-[#102210]" />
        <div className="absolute bottom-0 left-0 right-0 h-3/5 bg-gradient-to-t from-[#102210] via-[#102210] to-transparent" />
      </div>

      {/* Content Layer */}
      <div className="relative z-10 flex w-full flex-col h-full">
        {/* Top Row - Skip Button */}
        <div className="flex w-full items-center justify-between p-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] opacity-80">
          <div className="w-12" /> {/* Spacer */}
          <button
            onClick={onSkip}
            className="text-sm font-bold tracking-wide transition-colors uppercase active:scale-95 text-primary/80"
          >
            Skip
          </button>
        </div>

        {/* Main Content - Vertically Centered & Bottom Aligned */}
        <div className="flex flex-1 flex-col justify-end px-6 pb-10">
          {/* Icon Container */}
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full backdrop-blur-sm bg-primary/10 border border-primary/20 shadow-[0_0_30px_rgb(from_var(--color-primary)_r_g_b_/_0.2)]">
            <span className="material-symbols-outlined text-[40px] text-primary">
              savings
            </span>
          </div>

          {/* Pagination Dots - Centered */}
          <div className="flex w-full flex-row items-center justify-center gap-3 mb-6">
            <PaginationDots total={3} activeIndex={2} />
          </div>

          {/* Title */}
          <h1 className="text-white tracking-tight text-[36px] md:text-[42px] font-extrabold leading-[1.1] text-center mb-4 drop-shadow-lg">
            Consistency <span className="text-primary">Pays.</span>
            <br />
            Literally.
          </h1>

          {/* Description */}
          <p className="text-gray-300 text-base md:text-lg font-light leading-relaxed text-center mb-10 px-2 max-w-sm mx-auto">
            Hit your goals to keep your stake. Win cash from those who missed
            theirs. Real fitness, real payouts.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-4 w-full">
            {/* Primary CTA - Start Winning */}
            <button
              onClick={onLogin}
              disabled={isLoading}
              className="group relative w-full overflow-hidden rounded-full p-4 transition-transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed bg-primary shadow-lg shadow-primary/20"
            >
              {/* Hover Effect */}
              <div className="absolute inset-0 bg-white/20 translate-y-full transition-transform group-hover:translate-y-0" />
              <div className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <LoadingSpinner size="md" />
                    <span className="text-[#102210] font-bold text-lg">
                      Loading...
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-[#102210] font-bold text-lg">
                      Start Winning
                    </span>
                    <span className="material-symbols-outlined text-[#102210] font-bold w-6 h-6 flex items-center justify-center">
                      arrow_forward
                    </span>
                  </>
                )}
              </div>
            </button>

            {/* Secondary CTA - Already have account */}
            <button
              onClick={onLogin}
              disabled={isLoading}
              className="w-full p-3 text-white/60 font-medium text-sm hover:text-white transition-colors disabled:opacity-50"
            >
              I already have an account
            </button>
          </div>
        </div>
      </div>

      {/* Left Swipe Indicator */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 opacity-40 z-20">
        <div className="h-12 w-1 rounded-full bg-white/50 animate-pulse" />
      </div>
    </div>
  );
}
